import { create } from 'zustand'
import type {
  Project,
  UploadImage,
  AssetPack,
  RenderJob,
  AnalysisProgress,
  AnalysisStep,
  ProductPreInfo,
} from '@/types/project'

interface ProjectState {
  // Current step in the flow
  currentStep: 'upload' | 'analyze' | 'materials' | 'template' | 'render'

  // Project data
  projectId: string | null
  project: Project | null
  uploadedImages: UploadImage[]
  assetPack: AssetPack | null

  // Template selection
  selectedTemplateId: string | null
  selectedAspectRatio: '9:16' | '16:9' | '1:1' | '4:5'
  selectedBrandId: string | null
  selectedDedicatedModelId: string | null

  // Analysis state
  analysisProgress: AnalysisProgress

  // Background analysis tracking (for completion toast when user navigates away)
  activeBackgroundAnalysis: { projectId: string; projectName: string } | null
  isAnalyzingPageMounted: boolean
  analysisToasts: Array<{ id: string; projectId: string; projectName: string; createdAt: number }>

  // Render jobs
  renderJobs: RenderJob[]
  activeRenderJobId: string | null

  // Loading states
  isLoading: boolean
  error: string | null

  // Pre-analysis info (passed from UploadPage to AnalyzingPage)
  pendingPreInfo: ProductPreInfo | null

  // Actions
  setCurrentStep: (step: ProjectState['currentStep']) => void
  setProjectId: (id: string | null) => void
  setProject: (project: Project | null) => void
  addUploadedImages: (images: UploadImage[]) => void
  removeUploadedImage: (imageId: string) => void
  clearUploadedImages: () => void
  setAssetPack: (assetPack: AssetPack | null) => void
  setSelectedTemplate: (templateId: string) => void
  setSelectedAspectRatio: (ratio: '9:16' | '16:9' | '1:1' | '4:5') => void
  setSelectedBrand: (brandId: string | null) => void
  setSelectedDedicatedModel: (modelId: string | null) => void
  updateAnalysisProgress: (progress: Partial<AnalysisProgress>) => void
  setActiveBackgroundAnalysis: (data: { projectId: string; projectName: string } | null) => void
  setAnalyzingPageMounted: (mounted: boolean) => void
  addAnalysisToast: (toast: { id: string; projectId: string; projectName: string; createdAt: number }) => void
  dismissAnalysisToast: (id: string) => void
  addRenderJob: (job: RenderJob) => void
  updateRenderJob: (jobId: string, updates: Partial<RenderJob>) => void
  setRenderJobs: (jobs: RenderJob[]) => void
  setActiveRenderJob: (jobId: string | null) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  setPendingPreInfo: (preInfo: ProductPreInfo | null) => void
  setMainImage: (imageId: string) => void
  reset: () => void
}

const initialAnalysisProgress: AnalysisProgress = {
  status: 'idle',
  currentStep: 'idle',
  progress: 0,
  message: '',
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  // Initial state
  currentStep: 'upload',
  projectId: null,
  project: null,
  uploadedImages: [],
  assetPack: null,
  selectedTemplateId: 'SimpleSlide5',
  selectedAspectRatio: '9:16',
  selectedBrandId: null,
  selectedDedicatedModelId: null,
  analysisProgress: initialAnalysisProgress,
  activeBackgroundAnalysis: null,
  isAnalyzingPageMounted: false,
  analysisToasts: [],
  renderJobs: [],
  activeRenderJobId: null,
  isLoading: false,
  error: null,
  pendingPreInfo: null,

  // Actions
  setCurrentStep: (step) => set({ currentStep: step }),

  setProjectId: (id) => set({ projectId: id }),

  setProject: (project) => set({
    project,
    projectId: project?.id || null,
    uploadedImages: project?.uploadedImages || [],
    assetPack: project?.assetPack || null,
    renderJobs: project?.renderJobs || [],
    selectedTemplateId: project?.selectedTemplate || 'SimpleSlide5',
    selectedBrandId: project?.selectedBrandId ?? null,
    selectedDedicatedModelId: project?.selectedDedicatedModelId ?? null,
  }),

  addUploadedImages: (images) => set((state) => ({
    uploadedImages: [...state.uploadedImages, ...images],
  })),

  removeUploadedImage: (imageId) => set((state) => ({
    uploadedImages: state.uploadedImages.filter((img) => img.id !== imageId),
  })),

  clearUploadedImages: () => set({ uploadedImages: [] }),

  setAssetPack: (assetPack) => set({ assetPack }),

  setSelectedTemplate: (templateId) => set({ selectedTemplateId: templateId }),

  setSelectedAspectRatio: (ratio) => set({ selectedAspectRatio: ratio }),

  setSelectedBrand: (brandId) => set({ selectedBrandId: brandId }),

  setSelectedDedicatedModel: (modelId) => set({ selectedDedicatedModelId: modelId }),

  updateAnalysisProgress: (progress) => set((state) => ({
    analysisProgress: { ...state.analysisProgress, ...progress },
  })),

  setActiveBackgroundAnalysis: (data) => set({ activeBackgroundAnalysis: data }),

  setAnalyzingPageMounted: (mounted) => set({ isAnalyzingPageMounted: mounted }),

  addAnalysisToast: (toast) => set((state) => ({
    analysisToasts: [...state.analysisToasts, toast],
  })),

  dismissAnalysisToast: (id) => set((state) => ({
    analysisToasts: state.analysisToasts.filter(t => t.id !== id),
  })),

  addRenderJob: (job) => set((state) => ({
    renderJobs: [...state.renderJobs, job],
    activeRenderJobId: job.id,
  })),

  updateRenderJob: (jobId, updates) => set((state) => ({
    renderJobs: state.renderJobs.map((job) =>
      job.id === jobId ? { ...job, ...updates } : job
    ),
  })),

  setRenderJobs: (jobs) => set({ renderJobs: jobs }),

  setActiveRenderJob: (jobId) => set({ activeRenderJobId: jobId }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  setPendingPreInfo: (preInfo) => set({ pendingPreInfo: preInfo }),

  setMainImage: (imageId) => set((state) => {
    const idx = state.uploadedImages.findIndex(img => img.id === imageId)
    if (idx <= 0) return state
    const reordered = [...state.uploadedImages]
    const [target] = reordered.splice(idx, 1)
    reordered.unshift(target)
    return { uploadedImages: reordered }
  }),

  reset: () => set({
    currentStep: 'upload',
    projectId: null,
    project: null,
    uploadedImages: [],
    assetPack: null,
    selectedTemplateId: 'SimpleSlide5',
    selectedAspectRatio: '9:16',
    selectedBrandId: null,
    selectedDedicatedModelId: null,
    analysisProgress: initialAnalysisProgress,
    activeBackgroundAnalysis: null,
    isAnalyzingPageMounted: false,
    analysisToasts: [],
    renderJobs: [],
    activeRenderJobId: null,
    isLoading: false,
    error: null,
    pendingPreInfo: null,
  }),
}))
